import type {
  Breadcrumb,
  ErrorEvent as SentryErrorEvent,
  Log as SentryLog,
  TransactionEvent as SentryTransactionEvent,
} from "@sentry/core";
import type { ReplayFrameEvent } from "@sentry/replay";

const SENSITIVE_FIELD_PATTERN = /token|secret|password|authorization|cookie|api[_-]?key/i;
const PERSONAL_FIELD_PATTERN = /email|phone|(?:first|last|full)?name|address|answers?|notes|description|comments?/i;
const TELEGRAM_BOT_TOKEN_URL_PATTERN = /(https?:\/\/api\.telegram\.org\/bot)[^/?#\s]+/gi;
const PUBLIC_LINK_TOKEN_PATH_PATTERN = /(\/(?:api\/)?(?:review-feedback|review-interaction|upload|review|r|leads\/intake)\/)[^/?#\s]+/gi;
const WEBHOOK_SECRET_PATH_PATTERN = /(\/hooks?\/)[^/?#\s]+/gi;
const SENSITIVE_QUERY_VALUE_PATTERN = /([?&](?:token(?:_hash)?|access_token|refresh_token|api[_-]?key|key|secret|password|authorization|cookie|code|state|signature|sig|email)=)[^&#\s]*/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const PHONE_PATTERN = /(?<![\w])(?:\+64|\+61|0)(?:[\s().-]*\d){8,10}(?!\d)/gi;
const STREET_ADDRESS_PATTERN = /\b\d{1,5}\s+(?:(?:[A-Z0-9][A-Z0-9'.-]*\s+){0,5})(?:Street|St|Road|Rd|Avenue|Ave|Lane|Ln|Drive|Dr|Place|Pl|Terrace|Tce|Court|Ct|Crescent|Cres|Parade|Pde|Boulevard|Blvd|Highway|Hwy)\b(?:,\s*[^;\n]+)?/gi;

function collectPiiTextValues(value: unknown): string[] {
  const values: string[] = [];
  const pending: Array<{ value: unknown; parentKey?: string }> = [
    { value, parentKey: undefined },
  ];
  const seenContexts = new WeakMap<object, Set<string | undefined>>();

  function wasSeen(value: object, parentKey?: string): boolean {
    let contexts = seenContexts.get(value);
    if (!contexts) {
      contexts = new Set();
      seenContexts.set(value, contexts);
    }
    if (contexts.has(parentKey)) return true;
    contexts.add(parentKey);
    return false;
  }

  while (pending.length > 0) {
    const current = pending.pop()!;
    if (typeof current.value === "string") {
      if (
        current.parentKey &&
        PERSONAL_FIELD_PATTERN.test(current.parentKey) &&
        current.value.trim()
      ) {
        values.push(current.value.trim());
      }
      continue;
    }

    if (Array.isArray(current.value)) {
      if (wasSeen(current.value, current.parentKey)) continue;
      for (let index = current.value.length - 1; index >= 0; index -= 1) {
        pending.push({ value: current.value[index], parentKey: current.parentKey });
      }
      continue;
    }

    if (current.value && typeof current.value === "object") {
      if (wasSeen(current.value, current.parentKey)) continue;
      const entries = Object.entries(current.value as Record<string, unknown>);
      for (let index = entries.length - 1; index >= 0; index -= 1) {
        const [key, item] = entries[index];
        const fieldKey = PERSONAL_FIELD_PATTERN.test(key) ? key : current.parentKey;
        pending.push({ value: item, parentKey: fieldKey });
      }
    }
  }

  return values;
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Redact free-text personal information, including exact values known to the caller. */
export function scrubPiiText(value: string, personalValues: string[] = []): string {
  const withKnownPiiRedacted = [...new Set(personalValues)]
    .filter((item) => item.trim().length >= 3)
    .sort((left, right) => right.length - left.length)
    .reduce((text, item) => {
      const matcher = new RegExp(
        `(^|[^\\p{L}\\p{N}])${escapeRegExp(item.trim())}(?=$|[^\\p{L}\\p{N}])`,
        "giu",
      );
      return text.replace(matcher, "$1[REDACTED_PII]");
    }, value);

  return withKnownPiiRedacted
    .replace(TELEGRAM_BOT_TOKEN_URL_PATTERN, "$1[REDACTED]")
    .replace(PUBLIC_LINK_TOKEN_PATH_PATTERN, "$1[REDACTED]")
    .replace(WEBHOOK_SECRET_PATH_PATTERN, "$1[REDACTED]")
    .replace(SENSITIVE_QUERY_VALUE_PATTERN, "$1[REDACTED]")
    .replace(/(\bBearer\s+)[^\s,;]+/gi, "$1[REDACTED]")
    .replace(/(https?:\/\/)[^/\s:@]+:[^/\s@]+@/gi, "$1[REDACTED]@")
    .replace(EMAIL_PATTERN, "[REDACTED_EMAIL]")
    .replace(PHONE_PATTERN, "[REDACTED_PHONE]")
    .replace(STREET_ADDRESS_PATTERN, "[REDACTED_ADDRESS]");
}

/** Collect field values so matching text elsewhere in an event can also be removed. */
export function collectSentryPiiValues(value: unknown): string[] {
  return collectPiiTextValues(value);
}

function scrubValue(
  value: unknown,
  parentKey?: string,
  seen = new WeakMap<object, unknown>(),
  personalValues: string[] = [],
): unknown {
  let result: unknown;
  const pending: Array<{
    value: unknown;
    parentKey?: string;
    assign: (value: unknown) => void;
  }> = [{ value, parentKey, assign: (scrubbed) => { result = scrubbed; } }];

  while (pending.length > 0) {
    const current = pending.pop()!;
    const currentValue = current.value;

    if (typeof currentValue === "string") {
      if (current.parentKey && SENSITIVE_FIELD_PATTERN.test(current.parentKey)) {
        current.assign("[REDACTED]");
      } else if (current.parentKey && PERSONAL_FIELD_PATTERN.test(current.parentKey)) {
        current.assign("[REDACTED_PII]");
      } else {
        current.assign(scrubPiiText(currentValue, personalValues));
      }
      continue;
    }

    if (Array.isArray(currentValue)) {
      const existing = seen.get(currentValue);
      if (existing) {
        current.assign(existing);
        continue;
      }

      const scrubbed: unknown[] = new Array(currentValue.length);
      seen.set(currentValue, scrubbed);
      current.assign(scrubbed);
      for (let index = currentValue.length - 1; index >= 0; index -= 1) {
        pending.push({
          value: currentValue[index],
          parentKey: undefined,
          assign: (item) => { scrubbed[index] = item; },
        });
      }
      continue;
    }

    if (currentValue && typeof currentValue === "object") {
      const existing = seen.get(currentValue);
      if (existing) {
        current.assign(existing);
        continue;
      }

      const record = currentValue as Record<string, unknown>;
      const scrubbed: Record<string, unknown> = {};
      const entries = Object.entries(record);
      seen.set(currentValue, scrubbed);
      current.assign(scrubbed);
      for (const [key] of entries) {
        Object.defineProperty(scrubbed, key, {
          value: undefined,
          enumerable: true,
          configurable: true,
          writable: true,
        });
      }
      for (let index = entries.length - 1; index >= 0; index -= 1) {
        const [key, item] = entries[index];
        pending.push({
          value: item,
          parentKey: key,
          assign: (scrubbedItem) => {
            Object.defineProperty(scrubbed, key, {
              value: scrubbedItem,
              enumerable: true,
              configurable: true,
              writable: true,
            });
          },
        });
      }
      continue;
    }

    current.assign(currentValue);
  }

  return result;
}

function scrubObject<T>(value: T): T {
  const personalValues = collectPiiTextValues(value);
  return scrubValue(value, undefined, new WeakMap<object, unknown>(), personalValues) as T;
}

/** Redact sensitive data in breadcrumbs before they are attached to events. */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb | null {
  return scrubObject(breadcrumb);
}

/** Scrub errors, request context, breadcrumbs, and extras before transmission. */
export function scrubPii(event: SentryErrorEvent): SentryErrorEvent | null {
  const scrubbed = scrubObject(event);

  if (scrubbed.user) {
    delete scrubbed.user.email;
    delete scrubbed.user.ip_address;
  }
  if (scrubbed.request?.headers) {
    // Common headers that carry the caller's IP on Vercel / behind proxies.
    const headers = { ...scrubbed.request.headers };
    for (const key of Object.keys(headers)) {
      const lower = key.toLowerCase();
      if (
        lower === "x-forwarded-for" ||
        lower === "x-real-ip" ||
        lower === "x-vercel-forwarded-for" ||
        lower === "cf-connecting-ip"
      ) {
        delete headers[key];
      }
    }
    scrubbed.request.headers = headers;
  }

  return scrubbed;
}

/** Apply the same filtering to sampled performance transactions. */
export function scrubTransaction(
  event: SentryTransactionEvent,
): SentryTransactionEvent | null {
  return scrubObject(event);
}

/** Apply the same filtering to Sentry's structured log events. */
export function scrubLog(log: SentryLog): SentryLog | null {
  return scrubObject(log);
}

/** Scrub URLs and user details from session replay recording events. */
export function scrubReplayEvent(event: ReplayFrameEvent): ReplayFrameEvent | null {
  return scrubObject(event);
}
